import { isFulfilled } from "@reduxjs/toolkit";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useImpersonation } from "../../../../contexts/ImpersonationContext";
import useFileNameLength from "../../../../hooks/useFileNameLength";
import Roles from "../../../../models/roles";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../../store/application/slice";
import { TUploadedDocumentsData } from "../../../../store/onboarding/types";
import {
  deleteCandidateDocumentThunk,
  deleteMakeARequestFile,
  uploadsCandidateDocumentThunk,
  uploadsMakeARequestDocumentThunk,
} from "../../../../store/uploads/thunks";
import {
  TDeleteFileApiResponse,
  TFileUploadsResponse,
  TRequestDetails,
  TUploadedDocument,
} from "../../../../store/uploads/types";
import { formatFileName } from "../../../../utils/stringUtils";
import Cancel from "../../Icons/Cancel";
import FileUpload from "../../Icons/FileUpload";
import FileUploaded from "../../Icons/FileUploaded";
import ErrorMessage from "../../Tooltips/ErrorMessage";

/**
 * Accepted file types for document upload
 */
const ACCEPTED_FILE_TYPES = {
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  png: "image/png",
} as const;

/**
 * Maximum file size in bytes (5MB)
 */
const MAX_FILE_SIZE = 5 * 1024 * 1024;

/**
 * Defines a type alias for a function named `OnFileSelect`.
 * This function accepts two parameters:
 * 1. `file` of type `TFileUploadsResponse`, representing the response of a file upload.
 * 2. `fileName` of type `string`, representing the name of the file.
 * The function does not return any value (`void`)
 */
type OnFileSelect = (
  file: TFileUploadsResponse,
  fileName: string,
  deleteFile: boolean
) => void;

type onMakeARequestFileSelect = (
  file: TRequestDetails,
  fileName: string,
  deleteFile: boolean
) => void;

type OnFileValidated = (file: File, fileName: string) => void;

/**
 * DocumentUpload Props Interface
 * @interface DocumentUploadProps
 */
interface DocumentUploadProps {
  /** Callback when file is selected and validated */
  onFileSelect?: OnFileSelect;
  /** Type of document being uploaded */
  type: string;
  /** Whether the upload is required */
  required?: boolean;
  /** Additional accepted file types */
  additionalAcceptedTypes?: string[];
  /** Custom max file size in bytes */
  maxFileSize?: number;
  /** */
  onMakeARequestFileSelect?: onMakeARequestFileSelect;
  /** request id have to pass when uploading second subsequent attachments*/
  makeARequestId?: number | null | string;
  /** */
  attachmentList?: TUploadedDocument | null;
  /** Candidate UUID - used when super addmin edi candidate details when they got make a request */
  candidate_uuid?: string | null;
  /** passing file name from parent component */
  uploadedFileName?: string | null;

  fileUrl?: string | null; // This prop is not used in the component but can be passed for future use
  /** to disable the upload functionality */
  disabled?: boolean;
  showFileUploadLabel?: boolean;
  dontUploadImmediately?: boolean;
  onFileValidated?: OnFileValidated;
  removeIcon?: boolean;
  isPopup?: boolean;
  openInNewTab?: boolean;
}

/**
 * DocumentUpload Component
 * A reusable file upload component with built-in validation for file types and size
 */
const DocumentUpload: FC<DocumentUploadProps> = ({
  onFileSelect,
  type,
  required = false,
  additionalAcceptedTypes = [],
  maxFileSize = MAX_FILE_SIZE,
  onMakeARequestFileSelect,
  makeARequestId,
  attachmentList,
  candidate_uuid = null,
  uploadedFileName = null,
  fileUrl = null, // This prop is not used in the component but can be passed for future use,
  disabled = false,
  showFileUploadLabel = false,
  dontUploadImmediately = false,
  onFileValidated,
  removeIcon,
  isPopup,
  openInNewTab = false,
}) => {
  const dispatch = useDispatch();

  const user = useSelector((state) => state.auth.user);
  const profileData = useSelector((state) => state.onboarding.personaldetails);
  const reviewCandidateData = useSelector(
    (state) => state.superadmin.reviewCandidateData
  );
  const {isImpersonating} = useImpersonation();
  const { maxLength, preserveEndChars } = useFileNameLength(isPopup ?? false);

  // Check if the logged-in user is a Super Admin (used for editing candidate profiles)
  const isSuperAdmin = (user?.role?.name as Roles) === Roles.SUPER_ADMIN ||
      (user?.role?.name as Roles) === Roles.GENERAL_ADMIN;

  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [makeARequestAttachment, setMakeARequestAttachment] = useState<
    TUploadedDocument[] | null
  >(null);

  const inputId = `file-upload-${type}`;

  /** Get the values from the state */
  const personaldetails = useSelector(
    (state) => !isSuperAdmin ? state.onboarding.personaldetails : state.superadmin.reviewCandidateData?.profile
  );
  const qualifications = useSelector(
    (state) => !isSuperAdmin ? state.onboarding.qualifications : state.superadmin.reviewCandidateData?.qualifications
  );

  const acceptedTypes = useMemo(() => {
    const types: string[] = [
      ACCEPTED_FILE_TYPES.pdf,
      ACCEPTED_FILE_TYPES.jpeg,
      ACCEPTED_FILE_TYPES.jpg,
      ACCEPTED_FILE_TYPES.png,
    ];

    if (type === "resume") {
      types.push(ACCEPTED_FILE_TYPES.doc, ACCEPTED_FILE_TYPES.docx); // allow .doc, .docx only for resumes
    }

    return [...types, ...additionalAcceptedTypes];
  }, [type, additionalAcceptedTypes]);

  let docName: TUploadedDocumentsData | undefined = undefined;
   if(!dontUploadImmediately) {
    if(isSuperAdmin){
      docName = reviewCandidateData?.documents.find(
          (file: TUploadedDocumentsData) => file.document_type === type
        );
    }else{
      docName = qualifications?.documents.find(
        (file: TUploadedDocumentsData) => file.document_type === type
      );
    }
  }
  
  const fileName = type === "resume" && personaldetails?.resume_name;

  const validateFile = useCallback(
    (uploadedFile: File): string | null => {
      if (!acceptedTypes.includes(uploadedFile.type)) {
        const allowedExtensions = Object.entries(ACCEPTED_FILE_TYPES)
          .filter(([ext, mime]) => acceptedTypes.includes(mime) && ext !== "jpg")
          .map(([ext]) => ext.toUpperCase())
          .join(", ");

        return `Invalid file type. Please upload ${allowedExtensions}.`;
      }

      if (uploadedFile.size > maxFileSize) {
        return `File size exceeds the ${maxFileSize / (1024 * 1024)}MB limit.`;
      }

      return null;
    },
    [acceptedTypes, maxFileSize]
  );

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const uploadedFile = event.target.files?.[0];
    setError(null);

    if (!uploadedFile) {
      return;
    }

    const validationError = validateFile(uploadedFile);
    if (validationError) {
      setError(validationError);
      setFile(null);
      event.target.value = ""; // Reset input
      return;
    }

    // If dontUploadImmediately is true, just validate and send file to parent
    if (dontUploadImmediately) {
      if (onFileValidated) {
        onFileValidated(uploadedFile, uploadedFile.name);
        setFile(uploadedFile);
      } else {
        console.warn("onFileValidated callback is not provided when dontUploadImmediately is true");
      }
      return;
    }

    try {
      dispatch(showTransparentPreloaderAction());
      setIsUploading(true);
      if (type === "make_a_request") {
        const data = new FormData();
        data.append("attachment", uploadedFile);
        if (makeARequestId !== undefined && makeARequestId !== null) {
          data.append("request_id", makeARequestId.toString());
        }
        const result = await dispatch(uploadsMakeARequestDocumentThunk(data));
        if (onMakeARequestFileSelect && isFulfilled(result)) {
          setMakeARequestAttachment(result.payload?.data?.documents);
          onMakeARequestFileSelect(
            result.payload?.data,
            uploadedFile.name,
            false
          );
          setFile(uploadedFile);
        }
      } else {
        const data = new FormData();
        data.append("document", uploadedFile);
        data.append("document_type", type);
        if (candidate_uuid !== undefined && candidate_uuid !== null) {
          data.append("candidate_uuid", candidate_uuid.toString());
          if(!isImpersonating) {
            data.append("isSuperAdmin", "1");
          }
        }
       
        const result = await dispatch(
          uploadsCandidateDocumentThunk({
            data,
            isSuperAdmin: candidate_uuid !== undefined && candidate_uuid !== null ? "1" : "0",
            isImpersonating: isImpersonating,
          })
        );
        if (onFileSelect && isFulfilled(result)) {
          onFileSelect(result.payload, uploadedFile.name, false);
          setFile(uploadedFile);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upload file");
      setFile(null);
    } finally {
      setIsUploading(false);
    }
    dispatch(hideTransparentPreloaderAction());
  };

  const handleRemoveFile = async () => {
    const nullFile = document.getElementById(inputId) as HTMLInputElement;

    // If dontUploadImmediately is true, just clear local state
    if (dontUploadImmediately) {
      setFile(null);
      setError(null);
      if (nullFile) {
        nullFile.value = "";
      }
      // Optionally notify parent about removal
      if (onFileValidated) {
        onFileValidated(null as any, ""); // Or create a separate onFileRemoved callback
      }
      return;
    }

    // Original delete logic for uploaded files
    dispatch(showTransparentPreloaderAction());

    // Build payload conditionally
    const payload: { document_type: string; candidate_uuid?: string | null; isImpersonating: boolean } = {
      document_type: type,
      isImpersonating: isImpersonating
    };
    if (candidate_uuid !== null && candidate_uuid !== undefined) {
      payload.candidate_uuid = candidate_uuid;
    }
    payload.isImpersonating = isImpersonating;

    const deleteFileResponse = await dispatch(
      deleteCandidateDocumentThunk(payload)
    );
    
    // Check if the delete action was fulfilled
    if (isFulfilled(deleteFileResponse)) {
      const response = deleteFileResponse.payload as TFileUploadsResponse;
      if (onFileSelect) {
        onFileSelect(response, "", true);
      }
    }
    setFile(null);
    setError(null);
    uploadedFileName = null;

    if (nullFile) {
      nullFile.value = ""; // Set value to an empty string instead of null
    }
    dispatch(hideTransparentPreloaderAction());
  };

  const handleRemoveMakeRequestFile = async (attachmentId: number) => {
    const nullFile = document.getElementById(inputId) as HTMLInputElement;
    dispatch(showTransparentPreloaderAction());
    const deleteFileResponse = await dispatch(
      deleteMakeARequestFile(attachmentId)
    );
    if (isFulfilled(deleteFileResponse)) {
      const response = deleteFileResponse.payload as TDeleteFileApiResponse;
      setMakeARequestAttachment(response?.data?.documents);
      if (onMakeARequestFileSelect) {
        onMakeARequestFileSelect(response.data, "", true);
      }
    }

    setFile(null);
    setError(null);
    if (nullFile) {
      nullFile.value = ""; // Set value to an empty string instead of null
    }
    dispatch(hideTransparentPreloaderAction());
  };

  const handleDownloadDocument = (url: string | null) => {
    if((url === null || url === undefined || url?.length === 0)) {return;}
    if (url) {
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      if (!openInNewTab) {
        link.setAttribute("download", "document.pdf");
      }
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      console.error("No URL provided for download.");
    }
  };

  // Get the review status for the current profile
  const reviewStatus = profileData?.review_status;

  /**
   * Determines if the remove button should be hidden based on status and user role
   * - Super Admin during impersonation: Can ALWAYS remove (show remove button)
   * - Super Admin not impersonating: Can ALWAYS remove (show remove button)
   * - Candidate with "approved" status: CANNOT remove (hide remove button)
   * - Candidate with "waiting" or null status: CAN remove (show remove button)
   */
  const shouldHideRemoveButton = () => {
    // Super Admin during impersonation can always remove
    if (isImpersonating) {
      return false;
    }
    
    // Super Admin not impersonating (direct edit)
    if (isSuperAdmin) {
      return false;
    }
    
    // For candidates: only hide if status is "approved" (completed and approved)
    // return reviewStatus === 'approved';
  }

  useEffect(() => {
    showFileUploadLabel && setError(null);
  }, [showFileUploadLabel]);

  return (
    <>
      <div
        key={makeARequestAttachment?.length || 0}
        className="d-flex flex-column gap-10 document-upload"
        role="region"
        aria-label={`Upload ${type}`}
      >
        <input
          type="file"
          id={inputId}
          onChange={handleFileChange}
          className="visually-hidden"
          name={type}
          accept={acceptedTypes.join(",")}
          required={file || fileName || docName ? false : required}
          aria-required={file || fileName || docName ? false : required}
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-error` : undefined}
          disabled={isUploading || disabled}
        />

        {type === "make_a_request" ? (
          <>
            {makeARequestAttachment &&
              makeARequestAttachment?.length > 0 &&
              makeARequestAttachment?.map((attachments: TUploadedDocument) => (
                <div className="d-flex">
                  <div
                    key={attachments.id}
                    className="bg-off-white radius-8px px-20px py-8px d-flex gap-10 align-items-center fs-16 fw-400"
                  >
                    <FileUploaded />
                    <span className="text-black lh-sm text-break">
                      {formatFileName(attachments.file_name, maxLength, preserveEndChars)}
                    </span>
                    <div
                      onClick={() =>
                        handleRemoveMakeRequestFile(attachments.id)
                      }
                      className="d-flex gap-1 align-items-center cursor-pointer"
                      aria-label="Remove uploaded file"
                    >
                      <Cancel />
                      <div className="text-grey-medium">Remove</div>
                    </div>
                  </div>
                </div>
              ))}
            <div className="d-flex gap-10 align-items-center mt-10">
              <label
                htmlFor={inputId}
                className={`cursor-pointer bg-off-white radius-8px px-20px py-8px d-flex gap-10 align-items-center fs-16 fw-400`}
              >
                <FileUpload />
                Upload
              </label>
              <div className="fs-13 fw-400 text-grey-dark">
                Accepted file types: PDF, {(type as string) === "resume" ? "DOC, DOCX, " : ""} JPEG, PNG. Size limit: 5MB.
              </div>
            </div>
          </>
        ) : file || fileName || docName || uploadedFileName ? (
          <div className="d-flex">
            <div className="bg-off-white radius-8px px-20px py-8px d-flex gap-10 align-items-center fs-16 fw-400">
              <FileUploaded />
              <span className={`text-black lh-sm text-break ${fileUrl && fileUrl?.length > 0 ? "cursor-pointer" : ""}`}
              onClick={() => {handleDownloadDocument(fileUrl)}}>
                {formatFileName(
                  uploadedFileName ||
                  docName?.file_name ||
                  fileName ||
                  file?.name ||
                  "",
                  maxLength,
                  preserveEndChars
                )}
              </span>
              { (shouldHideRemoveButton() || removeIcon) ? "" : 
                <div
                  onClick={handleRemoveFile}
                  className="d-flex gap-1 align-items-center cursor-pointer"
                  aria-label="Remove uploaded file"
                >
                  <Cancel />
                  <div className="text-grey-medium">Remove</div>
                </div>
              }
            </div>
          </div>
        ) : (
          <div className="d-flex gap-10 align-items-center">
            <label
              htmlFor={inputId}
              className={`cursor-pointer bg-off-white radius-8px px-20px py-8px d-flex gap-10 align-items-center fs-16 fw-400`}
            >
              <FileUpload />
              Upload
            </label>
            <div className="fs-13 fw-400 text-grey-dark">
              Accepted file types: PDF, {(type as string) === "resume" ? "DOC, DOCX, " : ""} JPEG, PNG. Size limit: 5MB.
            </div>
          </div>
        )}

        {isUploading && (
          <div
            className="document-upload__loading"
            role="status"
            aria-label="Uploading document"
          >
            <div className="document-upload__spinner" />
          </div>
        )}
      </div>
      {error && <ErrorMessage message={error} />}
    </>
  );
};

export default DocumentUpload;
